import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, X } from 'lucide-react';
import { OGEET_MOMENTS } from '../data/ogeetMoments';

const OGeetMomentsPage = () => {
  const [zoomed, setZoomed] = useState(null);

  return (
    <main
      data-testid="ogeet-moments"
      className="relative min-h-screen bg-[#1A0F08] text-[#FFF8E1] overflow-x-hidden"
    >
      {/* Festival glows */}
      <div className="absolute -top-32 -left-32 w-[32rem] h-[32rem] rounded-full bg-[#FF1493] opacity-15 blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 -right-32 w-[32rem] h-[32rem] rounded-full bg-[#FFB300] opacity-15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 left-1/3 w-[32rem] h-[32rem] rounded-full bg-[#6A1B9A] opacity-15 blur-3xl pointer-events-none" />

      {/* Grain */}
      <div
        className="absolute inset-0 opacity-20 mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='400' height='400' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")",
        }}
      />

      {/* Back to OGeet */}
      <Link
        to="/ogeet"
        data-testid="back-to-ogeet"
        className="fixed top-6 left-6 md:top-8 md:left-8 z-40 flex items-center gap-2 text-[#FFF8E1] hover:text-[#FFD54F] transition-colors font-['Cormorant_Garamond'] tracking-[0.2em] uppercase text-xs md:text-sm group bg-black/40 backdrop-blur-md border border-[#FFD54F]/30 rounded-full px-4 py-2"
      >
        <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-1" />
        Back to OGeet
      </Link>

      {/* Header */}
      <section className="relative pt-24 md:pt-32 pb-8 md:pb-12 text-center px-6">
        <div className="flex items-center justify-center gap-3 mb-5">
          <span className="h-px w-10 bg-[#FFD54F]" />
          <span className="font-['Cormorant_Garamond'] italic text-sm tracking-[0.35em] uppercase text-[#FFD54F]">
            The Moments
          </span>
          <span className="h-px w-10 bg-[#FFD54F]" />
        </div>
        <h1 className="font-['Marcellus'] text-4xl md:text-6xl lg:text-7xl text-[#FFF8E1] mb-5 leading-tight">
          Snapshots &amp;
          <br />
          <span className="italic font-['Cormorant_Garamond'] text-[#FFD54F]">stories.</span>
        </h1>
        <p className="font-['Cormorant_Garamond'] italic text-lg md:text-xl text-[#FFF8E1]/80 max-w-2xl mx-auto">
          Little pieces of OGeet — evenings, plates, faces, and the in-between moments that make the room hum.
        </p>
      </section>

      {/* Polaroid scatter */}
      <section className="relative px-6 md:px-12 lg:px-20 pb-24 md:pb-32">
        <div className="relative max-w-[1200px] mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-10 sm:gap-y-16 gap-x-4 pt-6">
          {OGEET_MOMENTS.map((m, idx) => (
            <button
              key={idx}
              data-testid={`ogeet-moment-${idx}`}
              onClick={() => setZoomed(m)}
              style={{ transform: `rotate(${m.tilt}deg)` }}
              className="group relative bg-[#FFF8E1] p-3 pb-14 rounded-sm shadow-[0_20px_50px_rgba(0,0,0,0.55)] hover:shadow-[0_25px_70px_rgba(255,213,79,0.35)] transition-all duration-500 hover:!rotate-0 hover:scale-[1.03] cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#FFD54F] focus:ring-offset-2 focus:ring-offset-[#1A0F08] mx-auto max-w-[340px] w-full"
            >
              <div className="aspect-[4/5] overflow-hidden bg-black">
                <img
                  src={m.url}
                  alt={m.caption}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <p className="absolute left-0 right-0 bottom-4 text-center font-['Caveat',cursive] text-[#1C1917]/85 text-xl md:text-2xl px-3 truncate">
                {m.caption}
              </p>
              {/* Tape corners */}
              <div className="absolute -top-2 left-6 w-14 h-4 bg-[#FFD54F]/30 border border-[#FFD54F]/40 -rotate-6 pointer-events-none" />
              <div className="absolute -top-2 right-6 w-10 h-4 bg-[#FF1493]/25 border border-[#FF1493]/40 rotate-6 pointer-events-none" />
            </button>
          ))}
        </div>

        {/* Footnote */}
        <p className="text-center font-['Cormorant_Garamond'] italic text-[#FFF8E1]/50 mt-16 md:mt-20 text-sm md:text-base tracking-wider">
          ✦  More moments landing soon — follow us on Instagram for the daily.
        </p>
      </section>

      {/* Zoom lightbox */}
      {zoomed && (
        <div
          data-testid="ogeet-moment-zoom"
          className="fixed inset-0 z-50 bg-[#1A0F08]/95 backdrop-blur-sm flex flex-col items-center justify-center p-6 animate-fadeIn"
          onClick={() => setZoomed(null)}
        >
          <button
            data-testid="ogeet-moment-zoom-close"
            onClick={() => setZoomed(null)}
            className="absolute top-6 right-6 text-[#FFD54F] hover:text-[#FFF8E1] transition-colors"
            aria-label="Close"
          >
            <X size={32} />
          </button>
          <div
            className="bg-[#FFF8E1] p-4 pb-14 rounded-sm shadow-2xl max-w-3xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={zoomed.url}
              alt={zoomed.caption}
              className="w-full max-h-[75vh] object-contain bg-black"
            />
            <p className="text-center font-['Caveat',cursive] text-[#1C1917]/85 text-2xl md:text-3xl mt-3">
              {zoomed.caption}
            </p>
          </div>
        </div>
      )}
    </main>
  );
};

export default OGeetMomentsPage;
